import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityHasComponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isComponentType,
  isKind,
  isOrphan,
} from '@backstage/plugin-catalog';
import { Direction, EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { EntityGroupProfileCard, EntityMembersListCard, EntityOwnershipCard, EntityUserProfileCard } from '@backstage/plugin-org';
import { EntityTechdocsContent, isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { Grid } from '@material-ui/core';
import React from 'react';

import {
  EntityAzureGitTagsContent,
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  isAzureDevOpsAvailable,
} from '@backstage-community/plugin-azure-devops';

import { EntityLinguistCard } from '@backstage-community/plugin-linguist';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { EntityTodoContent } from '@backstage-community/plugin-todo';
import { EntityKubernetesContent, isKubernetesAvailable } from '@backstage/plugin-kubernetes';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { DocFeedbackAddon, DocHelpfulAddon, EntityDocFeedbackCard } from '@internal/plugin-doc-feedback';
import { DocTextSize, HideDocNavTitle } from '../techdocs';

/**
 * Tab order convention, applied to every entity kind (tabs that do not apply to a kind are omitted):
 *   Overview → Docs → DevOps → Quality → Deployment → API / Definition → Dependencies / Diagram
 *
 * Every Overview starts with the same "above the fold" row: About (description, owner, TechDocs link)
 * next to Links, followed by the relationship graph and kind-specific cards.
 *
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const techdocsContent = (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <EntityDocFeedbackCard />
    </Grid>
    <Grid item xs={12}>
      <EntityTechdocsContent>
        <TechDocsAddons>
          <HideDocNavTitle />
          <DocTextSize />
          <DocFeedbackAddon />
          <DocHelpfulAddon />
        </TechDocsAddons>
      </EntityTechdocsContent>
    </Grid>
  </Grid>
);

const devOpsContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12}>
      <EntityAzurePipelinesContent defaultLimit={25} />
    </Grid>
    <Grid item xs={12}>
      <EntityAzurePullRequestsContent defaultLimit={25} />
    </Grid>
    <Grid item xs={12}>
      <EntityAzureGitTagsContent />
    </Grid>
  </Grid>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

/** About (incl. owner and TechDocs link) and Links side by side, at the top of every Overview. */
const aboutAndLinksContent = (
  <>
    <Grid item xs={12} md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item xs={12} md={6}>
      <EntityLinksCard variant="gridItem" />
    </Grid>
  </>
);

const catalogGraphContent = (
  <Grid item xs={12}>
    <EntityCatalogGraphCard variant="gridItem" height={400} />
  </Grid>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    {aboutAndLinksContent}
    {catalogGraphContent}
    <Grid item xs={12}>
      <EntityLinguistCard />
    </Grid>
  </Grid>
);

const qualityContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12} md={6}>
      <EntitySonarQubeCard variant="gridItem" />
    </Grid>
    <Grid item xs={12} md={6}>
      <EntityTodoContent />
    </Grid>
  </Grid>
);

const apiContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12}>
      <EntityProvidedApisCard />
    </Grid>
    <Grid item xs={12}>
      <EntityConsumedApisCard />
    </Grid>
  </Grid>
);

const dependenciesContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item xs={12}>
      <EntityDependsOnComponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const mainEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/devOps" title="DevOps">
      {devOpsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/quality" title="Quality">
      {qualityContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route if={isKubernetesAvailable} path="/deployment" title="Deployment">
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      {dependenciesContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const servicePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/devOps" title="DevOps">
      {devOpsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/quality" title="Quality">
      {qualityContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isKubernetesAvailable} path="/deployment" title="Deployment">
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      {apiContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      {dependenciesContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const resourcePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {aboutAndLinksContent}
        {catalogGraphContent}
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/devOps" title="DevOps">
      {devOpsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const modulePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/devOps" title="DevOps">
      {devOpsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/quality" title="Quality">
      {qualityContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>{servicePage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('website')}>{mainEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('application')}>{mainEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('library')}>{mainEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('module')}>{modulePage}</EntitySwitch.Case>
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {aboutAndLinksContent}
        {catalogGraphContent}
        <Grid item xs={12}>
          <EntityProvidingComponentsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityConsumingComponentsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {aboutAndLinksContent}
        {catalogGraphContent}
        <Grid item xs={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        {/* <Grid item xs={12}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid> */}
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        {aboutAndLinksContent}
        {catalogGraphContent}
        <Grid item xs={12}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
